import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CommentForm from "../../components/comments/_commentForm";
import CommentList from "../../components/comments/_commentList";
import { useAuth } from "../../AuthContext";
import api from "../../api";

const ShowPost = () => {
  const { slug } = useParams();
  const { isAuthenticated, user } = useAuth();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  const imgUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/posts/show/${slug}`);
        setPost(res.data);
        setComments(res.data.comments || []);
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    if (modalImage) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [modalImage]);

  const handleCommentAdded = (newComment) => {
    setComments((prev) => [newComment, ...prev]);
  };

  const openModal = ({ src, alt }) => setModalImage({ src, alt });
  const closeModal = () => setModalImage(null);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-400">
        Erreur : {error.response?.data?.message || error.message}
      </div>
    );
  }

  if (!post) {
    return <div className="p-6 text-center text-gray-400">Post non trouvé.</div>;
  }

  const coverImage = post.image || post.thumbnail;
  const pageTitle = `${post.title} - LMFP Photography`;
  const pageDescription = post.description?.trim() || "Découvrez ce reportage photo signé LMFP Photography.";
  const pageImage = imgUrl(coverImage);
  const pageUrl = `${window.location.origin}/posts/${post.slug}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="LMFP Photography" />
        <meta property="og:locale" content="fr_FR" />
        {pageImage && <meta property="og:image" content={pageImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
      </Helmet>

      <div className="container mx-auto p-4">
        <article className="overflow-hidden rounded-2xl bg-gray-800 text-white shadow-lg">
          {coverImage && (
            <div
              className="relative h-[45vh] cursor-zoom-in overflow-hidden bg-black md:h-[55vh] lg:h-[65vh]"
              onClick={() => openModal({ src: imgUrl(coverImage), alt: `Couverture de ${post.title}` })}
            >
              <img
                src={imgUrl(coverImage)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
              />
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
              <div className="absolute inset-0 z-20 flex items-center justify-center p-6 md:p-10">
                <img
                  src={imgUrl(coverImage)}
                  alt={`Couverture de ${post.title}`}
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 z-30 p-6 md:p-10">
                <div className="max-w-4xl">
                  <p className="mb-3 text-sm uppercase tracking-[0.2em] text-orange-400 md:text-base">
                    LMFP Photography
                  </p>
                  <h1 className="mb-4 font-ptserif text-3xl font-bold leading-tight md:text-5xl">
                    {post.title}
                  </h1>
                  {post.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.categories.map((category) => (
                        <span
                          key={category}
                          className="rounded-full border border-orange-500/30 bg-orange-500/20 px-3 py-1 text-sm text-orange-200"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {post.description && (
            <div className="mb-2 ml-8 mt-4 max-w-3xl">
              <p className="text-base leading-relaxed text-gray-300 md:text-lg">
                {post.description}
              </p>
            </div>
          )}

          <div className="p-6 md:p-8">
            {!coverImage && (
              <div className="mb-8">
                <h1 className="mb-4 font-ptserif text-3xl font-bold md:text-4xl">{post.title}</h1>
                <p className="text-lg text-gray-300">{post.description}</p>
              </div>
            )}

            <section>
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-2xl font-bold md:text-3xl">Galerie</h2>
                {post.postImages?.length > 0 && (
                  <span className="text-sm text-gray-400">{post.postImages.length} image(s)</span>
                )}
              </div>

              {post.postImages?.length > 0 ? (
                <div className="columns-1 gap-4 space-y-4 md:columns-2 lg:columns-3">
                  {post.postImages.map((image) => {
                    const originalImage = image.path || image.fullImage;
                    const displayImage = originalImage || image.thumbnail || image.image;
                    return (
                      <div
                        key={image.id}
                        className="group relative block break-inside-avoid overflow-hidden rounded-xl bg-neutral-900 shadow-md transition duration-300 hover:shadow-xl"
                      >
                        <img
                          src={imgUrl(displayImage)}
                          alt={`${post.title} - vue ${image.order}`}
                          className="block h-auto w-full cursor-zoom-in transition duration-300 group-hover:scale-[1.02]"
                          loading="lazy"
                          decoding="async"
                          onClick={() => openModal({ src: imgUrl(originalImage || displayImage), alt: `${post.title} - vue ${image.order}` })}
                        />
                        <div className="pointer-events-none absolute inset-0 flex items-end bg-black/20 p-3 opacity-0 transition duration-300 group-hover:opacity-100">
                          <span className="block text-sm font-medium text-white">{post.title}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400">Aucune image dans la galerie.</p>
              )}
            </section>

            <div className="my-10 h-px w-full bg-gray-600/60" />

            <section>
              {isAuthenticated && (
                <CommentForm postId={post.id} onCommentAdded={handleCommentAdded} />
              )}
              <CommentList
                comments={comments}
                setComments={setComments}
                currentUser={user}
              />
            </section>
          </div>
        </article>

        {modalImage && (
          <div
            onClick={closeModal}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
          >
            <div onClick={(e) => e.stopPropagation()} className="relative animate-fadeIn">
              <button
                onClick={closeModal}
                className="absolute -right-4 -top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-2xl font-bold text-white transition hover:bg-black/80"
                aria-label="Fermer"
              >
                &times;
              </button>
              <img
                src={modalImage.src}
                alt={modalImage.alt || "Grande vue"}
                className="h-auto max-h-[95vh] w-auto max-w-[95vw] rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShowPost;